import {
  Alert,
  Checkbox,
  Divider,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { DialogConfirm } from 'components'
import _ from 'lodash'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { getScopesByUserRole, postCreateUser, putEditUser } from 'services/apiKYE'
import { USER_ROLE } from 'utils/constants/application'
import { USER_AUTH_METHOD_DESCRIPTIONS, USER_AUTH_METHOD_LABELS, USER_AUTH_METHODS, USER_ROLE_DESCRIPTION, USER_ROLE_LABELS, USER_ROLE_PERMISSIONS_SUMMARY } from 'utils/constants/user'
import { reportError, toastifyAxiosError } from 'utils/errorReporter'
import { ValidateEmail } from 'utils/validators'

const ConfigUserDialog = ({ open, userData, onClose, onSuccess }) => {
  const defaultBasicInfo = {
    name: '',
    email: '',
    enabled: true,
    user_role: USER_ROLE.compliance,
    auth_method: USER_AUTH_METHODS.SSO,
  }
  const [permissions, setPermissions] = useState({})
  const [basicInfo, setBasicInfo] = useState(defaultBasicInfo)
  const [basicInfoValidation, setBasicInfoValidation] = useState({ name: false, email: false })
  const [allActionsOpen, setAllActionsOpen] = useState(false)
  const [availableActions, setAvailableActions] = useState([])
  const [actionsLoading, setActionsLoading] = useState(false)
  const [actionsLoadFailed, setActionsLoadFailed] = useState(false)
  const token = useSelector((state) => state.auth.access_token)
  const isRecruitersApi = basicInfo.user_role === USER_ROLE.recruitersApi

  const onConfirm = () => {
    if (_.isEmpty(userData)) {
      postCreateUser(token, { ...basicInfo, permissions })
        .then((res) => {
          toast.success(res)
          onClose()
        })
        .catch((res) => {
          toastifyAxiosError(res, 'Ocorreu um erro ao criar o usuário')
          reportError(res)
        })
    } else {
      putEditUser(token, {
        ...basicInfo,
        new_email: basicInfo.email !== userData.email ? basicInfo.email : null,
        email: userData.email,
        permissions,
      })
        .then((res) => {
          toast.success(res)
          onSuccess()
        })
        .catch((res) => {
          toastifyAxiosError(res, 'Ocorreu um erro ao alterar o usuário')
          reportError(res)
        })
    }
  }

  useEffect(() => {
    if (!_.isEmpty(userData)) {
      const roleValue = Object.entries(USER_ROLE_LABELS).find(([, label]) => label === userData.user_role)?.[0]
      setBasicInfo({
        ...userData,
        user_role: roleValue || userData.user_role,
        auth_method: roleValue === USER_ROLE.recruitersApi || userData.user_role === USER_ROLE.recruitersApi
          ? USER_AUTH_METHODS.PASSWORD
          : userData.auth_method || USER_AUTH_METHODS.SSO,
      })
      setPermissions(userData.permissions)
    }
    else {
      setBasicInfo(defaultBasicInfo)
      setPermissions({})
    }
  }, [userData])

  useEffect(() => {
    if (!allActionsOpen || !open) return undefined

    let active = true
    setActionsLoading(true)
    setActionsLoadFailed(false)
    setAvailableActions([])

    getScopesByUserRole(token, basicInfo.user_role)
      .then((scopes) => {
        if (!Array.isArray(scopes)) {
          throw new Error('A resposta da API de ações permitidas é inválida.')
        }

        if (active) {
          setAvailableActions([...scopes].sort((first, second) => (
            (first.group || '').localeCompare(second.group || '')
            || (first.description || first.name || '').localeCompare(second.description || second.name || '')
          )))
        }
      })
      .catch((error) => {
        if (active) {
          setActionsLoadFailed(true)
          toastifyAxiosError(error, 'Ocorreu um erro ao listar as ações permitidas para esta função')
          reportError(error)
        }
      })
      .finally(() => {
        if (active) setActionsLoading(false)
      })

    return () => {
      active = false
    }
  }, [allActionsOpen, open, basicInfo.user_role, token])

  useEffect(() => {
    if (!open) setAllActionsOpen(false)
  }, [open])

  useEffect(() => {
    setBasicInfoValidation({  email: ValidateEmail(basicInfo.email),  name: basicInfo.name?.length > 5 })
  }, [basicInfo])

  const actionsByGroup = _.groupBy(availableActions, (action) => action.group || 'Outros')

  const textFieldHasError = (fieldName) => {
    const isInvalid = basicInfoValidation[fieldName] === false
    return !_.isEmpty(basicInfo[fieldName]) && isInvalid
  }

  const handleUserRoleChange = (userRole) => {
    setBasicInfo({
      ...basicInfo,
      user_role: userRole,
      auth_method: userRole === USER_ROLE.recruitersApi
        ? USER_AUTH_METHODS.PASSWORD
        : USER_AUTH_METHODS.SSO,
    })
  }


  return (
    <React.Fragment>
    <DialogConfirm
      customFullWidth={true}
      open={open}
      title={_.isEmpty(userData) ? 'Criar usuário' : 'Alterar usuário'}
      btnConfirmText={_.isEmpty(userData) ? 'Criar usuário' : 'Salvar alterações'}
      btnCancelText="Cancelar"
      onConfirm={onConfirm}
      onCancel={onClose}
      btnConfirmDisabled={_.some(basicInfoValidation, field => !field)}
    >
      {!_.isEmpty(userData) && (
        <div style={{ marginTop: 10, marginBottom: 15, fontSize: '1.35rem' }}>
          Atualizado em: {userData.updated && moment(userData.updated).isValid()
            ? moment(userData.updated).format('DD/MM/YYYY HH:mm:ss')
            : '-'}
        </div>
      )}

      <h4 style={{ fontWeight: 550, color: '#222' }}>Informações básicas</h4>


      <div style={{ marginTop: 10, display: 'flex', gap: 10, justifyContent: 'center' }}>
        <TextField
          variant="standard"
          label="Nome"
          placeholder="Nome"
          fullWidth={true}
          error={textFieldHasError('name')}
          helperText={textFieldHasError('name') && 'Nome inválido'}
          value={basicInfo.name}
          onChange={(ev) => setBasicInfo({ ...basicInfo, name: ev.target.value })}
        ></TextField>

        <TextField
          variant="standard"
          label="Email"
          placeholder="exemplo@email.com"
          fullWidth={true}
          error={textFieldHasError('email')}
          helperText={textFieldHasError('email') && 'E-mail inválido'}
          value={basicInfo.email}
          onChange={(ev) => setBasicInfo({ ...basicInfo, email: ev.target.value })}
        ></TextField>
      </div>

            {_.isEmpty(userData) && (
        <div style={{ margin: '20px 0' }}>
          <Alert severity="info" color="primary">
            Ao criar um usuário, o sistema enviará um email com instruções para o primeiro acesso.
          </Alert>
        </div>
      )}

      {!_.isEmpty(userData) && (
        <div style={{ margin: '20px 0' }}>
          <FormControlLabel
            label="Usuário Ativo"
            control={
              <Checkbox
                checked={basicInfo.enabled}
                onChange={() => setBasicInfo({ ...basicInfo, enabled: !basicInfo.enabled })}
              />
            }
          />
        </div>
      )}


      <div style={{ marginTop: 20 }}>
        <FormControl disabled={!basicInfo.enabled || isRecruitersApi} style={{ width: 300 }}>
          <InputLabel variant="standard" htmlFor="auth-method">
            Modo de Autenticação
          </InputLabel>
          <Select
            labelId="auth-method"
            id="auth-method"
            label="Modo de Autenticação"
            variant="standard"
            value={basicInfo.auth_method || USER_AUTH_METHODS.SSO}
            onChange={(ev) => setBasicInfo({ ...basicInfo, auth_method: ev.target.value })}
            fullWidth
          >
            {_.map(USER_AUTH_METHODS, (method) => (
              <MenuItem key={method} value={method}>
                {USER_AUTH_METHOD_LABELS[method]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <div style={{ margin: '10px 0' }}>{USER_AUTH_METHOD_DESCRIPTIONS[basicInfo.auth_method]}</div>
      </div>



      <div style={{ margin: '20px 0' }}>
        <FormControl disabled={!basicInfo.enabled} style={{ width: 300 }}>
          <InputLabel variant="standard" htmlFor="user-role">
            Função
          </InputLabel>
          <Select
            labelId="user-role"
            id="user-role"
            label="Função"
            variant="standard"
            value={basicInfo.user_role}
            onChange={(ev) => handleUserRoleChange(ev.target.value)}
            fullWidth={true}
          >
            {_.map(USER_ROLE, (role) => (
              <MenuItem key={role} value={role}>
                {USER_ROLE_LABELS[role] || role}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        
        <div style={{ margin: '10px 0' }}>{USER_ROLE_DESCRIPTION[basicInfo.user_role]}</div>


        <div style={{ margin: '30px 0' }}>
          <Divider></Divider>
        </div>


        <h4 style={{ fontWeight: 550, color: '#222' }}>
          Permissões da função {USER_ROLE_LABELS[basicInfo.user_role] || basicInfo.user_role}:
        </h4>

        <div style={{ marginTop: 10 }}>
          <ul >
            {_.map(USER_ROLE_PERMISSIONS_SUMMARY[basicInfo.user_role], (permission) => (
              <li key={permission}>
                {permission}
              </li>
            ))}
          </ul>

          { [USER_ROLE.admin, USER_ROLE.adminReadOnly, USER_ROLE.accessManager, USER_ROLE.recruitersApi].includes(basicInfo.user_role) && <div style={{ marginTop: 15 }}>
            <Alert severity="warning" color="primary">
              Usuários com função de {USER_ROLE_LABELS[basicInfo.user_role] || basicInfo.user_role} têm privilégios elevados, conceda com cuidado.
            </Alert>
          </div>}
        </div>
        <Button variant="text" style={{ marginTop: 15 }} onClick={() => setAllActionsOpen(true)}>
          Ver todas as ações de {USER_ROLE_LABELS[basicInfo.user_role] || basicInfo.user_role}
        </Button>
      </div>



    </DialogConfirm>
    <Dialog
      open={allActionsOpen}
      onClose={() => setAllActionsOpen(false)}
      fullWidth
      maxWidth="md"
      aria-labelledby="role-actions-dialog-title"
    >
      <DialogTitle id="role-actions-dialog-title">
        Ações permitidas para {USER_ROLE_LABELS[basicInfo.user_role] || basicInfo.user_role}
      </DialogTitle>
      <DialogContent dividers>
        {actionsLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
            <CircularProgress aria-label="Carregando ações permitidas" />
          </div>
        )}
        {!actionsLoading && actionsLoadFailed && (
          <Alert severity="error">Não foi possível carregar as ações permitidas.</Alert>
        )}
        {!actionsLoading && !actionsLoadFailed && availableActions.length === 0 && (
          <Typography>Não há ações permitidas para esta função.</Typography>
        )}
        {!actionsLoading && !actionsLoadFailed && _.map(actionsByGroup, (actions, group) => (
          <section key={group}>
            <Typography variant="h6" component="h3" sx={{ mt: 2 }}>
              {group}
            </Typography>
            <List dense disablePadding>
              {_.map(actions, (action) => (
                <ListItem key={action.name} disableGutters>
                  <ListItemText
                    primary={action.description}
                  />
                </ListItem>
              ))}
            </List>
          </section>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setAllActionsOpen(false)}>Fechar</Button>
      </DialogActions>
    </Dialog>
    </React.Fragment>
  )
}

export default ConfigUserDialog
